import { useEffect } from "react";
import { useQueryErrorToast } from "@/lib/use-query-error-toast";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { adoptAccountTheme } from "@/app/use-theme";
import { OfflineScreen } from "@/components/ui/OfflineScreen";
import { endSession, isSignedIn, rememberAdminId, SIGN_IN_PATH } from "@/lib/auth";
import { useOnline } from "@/lib/use-online";
import { useGetCurrentAdminQuery } from "../auth-api";
import { sessionCleared, sessionEstablished, sessionUnavailable } from "../session-slice";

/**
 * The gate, and the session loader.
 *
 * Sits above every authenticated route and sorts the ways a session can fail
 * to start, which are not the same kind of problem and must not get the same
 * screen:
 *
 *   - **No token.** Bounce to `/sign-in`, carrying where the person was going
 *     so they land there instead of on the dashboard.
 *   - **No network.** The offline screen. This is the one case where "check
 *     your connection" is the right advice, and `navigator.onLine` being
 *     `false` is the only signal trustworthy enough to give it.
 *   - **Token refused (401/403).** The base query has already tried the
 *     refresh token by the time a 401 lands here, so the session is over.
 *     Clear it and bounce, or the sign-in screen would redirect straight back
 *     here on the strength of it.
 *   - **Anything else.** The server did not answer. The panel renders anyway;
 *     the shell decides what to put in the page area. Throwing the whole
 *     interface away over one failed request makes a blip look like an outage,
 *     and the person is left on a dead end with nothing to click.
 *
 * `/me` is loaded rather than trusted from the token. The server resolves
 * permissions from the database on every request precisely so that a revoked
 * role takes effect immediately, and reading them from a claim here would
 * reintroduce the staleness that design avoids. Children render only once it
 * has landed: a nav bar with every section briefly visible and then collapsing
 * looks like a bug and, worse, advertises what someone cannot reach.
 */
export function RequireAuth() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const status = useAppSelector((state) => state.session.status);

  const signedIn = isSignedIn();
  const online = useOnline();

  const { data, isLoading, isError, error } = useGetCurrentAdminQuery(undefined, {
    skip: !signedIn,
  });

  const httpStatus =
    isError && typeof error === "object" && error !== null && "status" in error
      ? (error as { status: unknown }).status
      : null;

  const refused = httpStatus === 401 || httpStatus === 403;

  // A re-read of `/me` after the first one (a role change, a profile save)
  // that fails keeps the old copy in `data`, so the effect below would quietly
  // carry on with permissions that may be out of date. The first load has its
  // own screens; this is only for the refetch.
  useQueryErrorToast(
    { isError: isError && data !== undefined && !refused, error },
    "Couldn't refresh your permissions",
  );

  useEffect(() => {
    if (data) {
      dispatch(sessionEstablished(data));
      // So the roster can mark "(you)" on the first render after a reload,
      // before this query has come back a second time.
      rememberAdminId(data.id);
      // Only takes effect on a browser that has no preference of its own —
      // see the note on the function.
      adoptAccountTheme(data.theme);
    } else if (refused) {
      dispatch(sessionCleared());
    } else if (isError) {
      dispatch(sessionUnavailable());
    }
  }, [data, isError, refused, dispatch]);

  // Drop the dead token so the sign-in screen does not immediately redirect
  // back here on the strength of it.
  useEffect(() => {
    if (refused) endSession();
  }, [refused]);

  if (!signedIn || refused) {
    return (
      <Navigate
        to={`${SIGN_IN_PATH}?from=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );
  }

  // Ahead of the loading state: a request that is hanging because the network
  // went away should say so rather than spin.
  if (!online) return <OfflineScreen />;

  if (isLoading || status === "unknown") {
    return (
      <div className="flex h-full items-center justify-center" role="status" aria-live="polite">
        <span className="text-content-muted text-sm">Loading session…</span>
      </div>
    );
  }

  // Including the failure case. The shell renders, and swaps the page area for
  // a panel that says it cannot load — see `AppShell`.
  return <Outlet />;
}
